import {
  createUploader,
  type Uploader,
  type UploadTransport,
} from '@nucleus-commerce/admin-kit/uploads';
import {
  deleteAdminV1OrgMediaFoldersId,
  deleteAdminV1OrgMediaId,
  getAdminV1OrgMedia,
  getAdminV1OrgMediaFolders,
  getAdminV1OrgMediaId,
  patchAdminV1OrgMediaFoldersId,
  patchAdminV1OrgMediaId,
  postAdminV1OrgMediaFolders,
  postAdminV1OrgMediaFoldersIdItems,
  postAdminV1OrgMediaFoldersIdItemsRemove,
  postAdminV1OrgMediaIdConfirm,
  postAdminV1OrgMediaPresign,
  PostAdminV1OrgMediaPresignBodyContentType,
  type CreateMediaFolderRequest,
  type GetAdminV1OrgMedia200,
  type GetAdminV1OrgMediaParams,
  type Media,
  type MediaFolder,
  type MediaFolderItemsRequest,
  type MediaFolderList,
  type PatchAdminV1OrgMediaIdBody,
  type PresignMediaResponse,
  type UpdateMediaFolderRequest,
} from '@nucleus-commerce/api-client/admin';

import { MAX_MEDIA_BYTES } from '@/shared/media/limits';

/** MIME types the backend's presign accepts. */
export const ACCEPTED_MEDIA_TYPES = Object.values(PostAdminV1OrgMediaPresignBodyContentType);

export { MAX_MEDIA_BYTES };

export const mediaApi = {
  list: (params: GetAdminV1OrgMediaParams): Promise<GetAdminV1OrgMedia200> =>
    getAdminV1OrgMedia(params),
  get: (id: string): Promise<Media> => getAdminV1OrgMediaId({ id }),
  update: (id: string, body: PatchAdminV1OrgMediaIdBody): Promise<Media> =>
    patchAdminV1OrgMediaId({ id }, body),
  remove: (id: string): Promise<void> => deleteAdminV1OrgMediaId({ id }),
};

/** Folders are a flat, organization-wide grouping; a file can sit in several of them. */
export const mediaFolderApi = {
  list: (): Promise<MediaFolderList> => getAdminV1OrgMediaFolders(),
  create: (body: CreateMediaFolderRequest): Promise<MediaFolder> =>
    postAdminV1OrgMediaFolders(body),
  rename: (id: string, body: UpdateMediaFolderRequest): Promise<MediaFolder> =>
    patchAdminV1OrgMediaFoldersId({ id }, body),
  remove: (id: string): Promise<void> => deleteAdminV1OrgMediaFoldersId({ id }),
  addItems: (id: string, body: MediaFolderItemsRequest): Promise<MediaFolder> =>
    postAdminV1OrgMediaFoldersIdItems({ id }, body),
  removeItems: (id: string, body: MediaFolderItemsRequest): Promise<MediaFolder> =>
    postAdminV1OrgMediaFoldersIdItemsRemove({ id }, body),
};

function isAcceptedType(type: string): type is PostAdminV1OrgMediaPresignBodyContentType {
  return (ACCEPTED_MEDIA_TYPES as string[]).includes(type);
}

/**
 * presign → PUT bytes → confirm. The PUT goes straight to the upload URL the backend hands
 * out, outside the JSON transport; confirm returns the media row (deduped by checksum).
 */
export function createMediaUploader(
  transport?: UploadTransport,
): Uploader<PresignMediaResponse, Media> {
  return createUploader<PresignMediaResponse, Media>({
    accept: ACCEPTED_MEDIA_TYPES,
    maxSizeBytes: MAX_MEDIA_BYTES,
    ...(transport ? { transport } : {}),
    presign: (file) => {
      if (!isAcceptedType(file.type)) {
        return Promise.reject(new Error(`Unsupported type ${file.type}`));
      }
      return postAdminV1OrgMediaPresign({
        filename: file.name,
        contentType: file.type,
        bytes: file.size,
      });
    },
    confirm: (presigned) => postAdminV1OrgMediaIdConfirm({ id: presigned.mediaId }),
  });
}
